import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { modelsApi } from '../../services/api';
import { ModelOptions, VendorModel } from '../../types';
import { errorMessage } from '../../utils/errors';
import { money } from '../../utils/costs';

interface Props {
  notify: (message: string, severity?: 'success' | 'error' | 'warning' | 'info') => void;
  /** Changes when the user's resumes change, so the estimate is worked out again. */
  reloadKey?: string;
}

const perResume = (estimate: number | null): string => (estimate === null ? 'cost not known yet' : `about ${money(estimate)} per tailored resume`);

function modelLabel(model: VendorModel): string {
  if (model.input === null || model.output === null) return `${model.id} (price not known yet)`;
  const price = `${money(model.input)} in, ${money(model.output)} out per million tokens`;
  return model.estimate === null ? `${model.id} (${price})` : `${model.id}: ${perResume(model.estimate)} (${price})`;
}

/**
 * Which model writes the user's tailored resumes: JobFlo's own, or one of the
 * user's choice called with their own key. Every cost shown is worked out
 * from the user's own resume.
 */
const ModelSettings: React.FC<Props> = ({ notify, reloadKey = '' }) => {
  const [options, setOptions] = useState<ModelOptions | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<'jobflo' | 'own'>('jobflo');
  const [vendor, setVendor] = useState('');
  const [model, setModel] = useState('');
  const [models, setModels] = useState<VendorModel[] | null>(null);
  const [modelsError, setModelsError] = useState<string | null>(null);
  const [keyDraft, setKeyDraft] = useState('');
  const [keyError, setKeyError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await modelsApi.options();
      setOptions(data);
      setSource(data.preference.source);
      setVendor((current) => current || data.preference.vendor || data.vendors.find((v) => v.has_key)?.key || data.vendors[0]?.key || '');
      setModel((current) => current || data.preference.model);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the model settings.'));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const chosenVendor = options?.vendors.find((v) => v.key === vendor) ?? null;
  const hasKey = chosenVendor?.has_key === true;

  // The vendor's own list of models, read when the user has a key for it.
  useEffect(() => {
    if (source !== 'own' || !vendor || !hasKey) {
      setModels(null);
      return undefined;
    }
    const controller = new AbortController();
    setModels(null);
    setModelsError(null);
    modelsApi
      .vendorModels(vendor, controller.signal)
      .then((data) => setModels(data.models))
      .catch((e) => {
        if (!controller.signal.aborted) setModelsError(errorMessage(e, 'Could not list the models.'));
      });
    return () => controller.abort();
  }, [source, vendor, hasKey, reloadKey]);

  const saveKey = async () => {
    if (!vendor || !keyDraft.trim()) return;
    setBusy(true);
    setKeyError(null);
    try {
      const saved = await modelsApi.saveKey(vendor, keyDraft.trim());
      setKeyDraft('');
      notify(`Key saved. ${saved.models.toLocaleString('en-US')} models are available with it.`);
      await load();
    } catch (e) {
      setKeyError(errorMessage(e, 'The key could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  const removeKey = async () => {
    setBusy(true);
    try {
      await modelsApi.removeKey(vendor);
      setModel('');
      notify('Key removed.');
      await load();
    } catch (e) {
      notify(errorMessage(e, 'The key could not be removed.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const save = async (next: { source: 'jobflo' } | { source: 'own'; vendor: string; model: string }) => {
    setBusy(true);
    try {
      await modelsApi.setPreference(next);
      notify(next.source === 'jobflo' ? "Tailored resumes will use JobFlo's model." : `Tailored resumes will use ${next.model} with your key.`);
      await load();
    } catch (e) {
      notify(errorMessage(e, 'The choice could not be saved.'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const saved = options?.preference;
  const ownIsSaved = saved?.source === 'own' && saved.vendor === vendor && saved.model === model;

  return (
    <Card variant="outlined" component="section" aria-labelledby="model-settings-title">
      <CardContent>
        <Typography id="model-settings-title" variant="h6" fontWeight={600}>
          Tailored resumes: model and cost
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          A tailored resume is your own resume reordered and reworded for one job by a language model. Choose which model
          does it.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!options ? (
          !error && <Skeleton variant="rounded" height={140} />
        ) : (
          <Stack spacing={2}>
            {options.resume === null ? (
              <Alert severity="info">Upload a resume to see what tailoring it would cost.</Alert>
            ) : !options.resume.readable ? (
              <Alert severity="warning">
                The text of "{options.resume.label}" could not be read, so costs cannot be estimated from it. PDF and Word (.docx)
                files work.
              </Alert>
            ) : (
              <Typography variant="body2">
                Based on "{options.resume.label}" (about {options.resume.tokens.toLocaleString('en-US')} tokens), one tailored resume
                takes about {options.tokens.input.toLocaleString('en-US')} tokens of input and {options.tokens.output.toLocaleString('en-US')} of
                output.
              </Typography>
            )}

            <RadioGroup
              aria-label="Which model writes your tailored resumes"
              value={source}
              onChange={(e) => {
                const next = e.target.value as 'jobflo' | 'own';
                setSource(next);
                if (next === 'jobflo' && saved?.source !== 'jobflo') save({ source: 'jobflo' });
              }}
            >
              <FormControlLabel
                value="jobflo"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">JobFlo's model</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {`${options.jobflo.model}, ${perResume(options.jobflo.estimate)}. `}
                      {options.jobflo.available ? 'Counted against your JobFlo allowance; nothing to set up.' : 'Not switched on yet.'}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', '& .MuiRadio-root': { pt: 0.5 } }}
              />
              <FormControlLabel
                value="own"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">A model of my choice, with my own key</Typography>
                    <Typography variant="body2" color="text.secondary">
                      You pay the vendor directly. Your key is stored encrypted and never shown again.
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mt: 1, '& .MuiRadio-root': { pt: 0.5 } }}
              />
            </RadioGroup>

            {source === 'own' && (
              <Stack spacing={2} sx={{ pl: { sm: 4 } }}>
                <TextField
                  select
                  size="small"
                  label="Vendor"
                  value={vendor}
                  onChange={(e) => {
                    setVendor(e.target.value);
                    setModel('');
                    setKeyDraft('');
                    setKeyError(null);
                  }}
                  sx={{ maxWidth: 360 }}
                >
                  {options.vendors.map((v) => (
                    <MenuItem key={v.key} value={v.key}>
                      {v.label}
                      {v.has_key ? ' (key saved)' : ''}
                    </MenuItem>
                  ))}
                </TextField>

                {hasKey ? (
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    <Typography variant="body2">Key saved, ending in {chosenVendor?.last4}.</Typography>
                    <Button size="small" color="inherit" disabled={busy} onClick={removeKey}>
                      Remove key
                    </Button>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        size="small"
                        type="password"
                        label={`Your ${chosenVendor?.label ?? ''} API key`}
                        value={keyDraft}
                        onChange={(e) => setKeyDraft(e.target.value)}
                        autoComplete="off"
                        error={Boolean(keyError)}
                        helperText={keyError ?? undefined}
                        sx={{ flex: 1, maxWidth: 480 }}
                      />
                      <Button variant="contained" disabled={busy || !keyDraft.trim()} onClick={saveKey} sx={{ alignSelf: { sm: 'flex-start' }, height: 40 }}>
                        {busy ? <CircularProgress size={18} color="inherit" /> : 'Check and save'}
                      </Button>
                    </Stack>
                    {chosenVendor && (
                      <Typography variant="caption" color="text.secondary">
                        Create a key at{' '}
                        <Link href={chosenVendor.keys_url} target="_blank" rel="noopener noreferrer">
                          {chosenVendor.keys_url.replace(/^https:\/\//, '')}
                        </Link>
                        . It is checked with the vendor before it is saved.
                      </Typography>
                    )}
                  </Stack>
                )}

                {hasKey && (
                  <>
                    {modelsError && <Alert severity="warning">{modelsError}</Alert>}
                    <TextField
                      select
                      size="small"
                      label="Model"
                      value={models?.some((m) => m.id === model) ? model : ''}
                      onChange={(e) => setModel(e.target.value)}
                      disabled={!models}
                      helperText={models ? "The vendor's current models, cheapest first. New ones appear here by themselves." : modelsError ? undefined : 'Asking the vendor for its models...'}
                    >
                      {(models ?? []).map((m) => (
                        <MenuItem key={m.id} value={m.id}>
                          {modelLabel(m)}
                        </MenuItem>
                      ))}
                    </TextField>
                    <Box>
                      <Button variant="contained" disabled={busy || !model || ownIsSaved} onClick={() => save({ source: 'own', vendor, model })}>
                        {ownIsSaved ? 'In use' : 'Use this model'}
                      </Button>
                    </Box>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

export default ModelSettings;
